/**
 * Prepaid tracking dashboard — staff view of prepaid cellular coverage across
 * every customer. Status stat cards (click-to-filter) over the shared
 * PrepaidCoverageGrid, with customer scoping through the grid's own toolbar
 * Customer filter — the same filter-bar shape every other grid page uses.
 * `?customer=<id>` deep-links the page pre-scoped (the billing account
 * shell's Pre-Paid tab renders its own scoped subview instead of linking
 * here, but shared links and bookmarks still land scoped).
 *
 * Contains:
 * - PrepaidDashboardView — routed page at /billing/prepaid-tracking (default export)
 */
import { Box } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import {
  BillingTitleBar,
  PREPAID_STATUS_CARD_COUNT,
  PrepaidCoverageGrid,
  PrepaidStatusCards,
} from '@sophocles/views/billing/components';

function PrepaidDashboardView() {
  const { t } = useTranslation();
  // ?customer=<id> deep-links the dashboard pre-scoped; the toolbar Customer
  // filter owns the value from there on.
  const [searchParams] = useSearchParams();
  const [selectedCustomerId, setSelectedCustomerId] = useState(() => {
    const fromParam = Number(searchParams.get('customer'));
    return Number.isInteger(fromParam) && fromParam > 0 ? fromParam : null;
  });
  const [statusFilter, setStatusFilter] = useState('');
  // Bumped by the grid's onDataChange so the cards recount after a write,
  // without the cards owning the grid's mutation lifecycle.
  const [reloadToken, setReloadToken] = useState(0);

  return (
    <>
      <BillingTitleBar title={t('billing.prepaidDashboard.title')} />

      <Box sx={{ flexShrink: 0, px: 'var(--page-px)', pt: 'var(--page-py)' }}>
        <PrepaidStatusCards
          customerId={selectedCustomerId}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          reloadToken={reloadToken}
          columns={{ xs: 2, sm: 3, md: PREPAID_STATUS_CARD_COUNT }}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          px: 'var(--page-px)',
          pb: 'var(--page-py)',
        }}
      >
        <PrepaidCoverageGrid
          customerId={selectedCustomerId}
          onCustomerChange={setSelectedCustomerId}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          onDataChange={() => setReloadToken(token => token + 1)}
        />
      </Box>
    </>
  );
}

export default PrepaidDashboardView;
